import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FadeUp } from "@/components/Reveal";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#121210] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-emerald-500/50 focus:shadow-[0_0_15px_rgba(16,185,129,0.1)] transition-all duration-200";

export default function AuthPage() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
      setUser(data);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="auth-page" className="pt-28 md:pt-16 min-h-screen flex items-center justify-center relative overflow-hidden">
      <Seo title="Sign in — HighRank" siteName="HighRank" description="Sign in to HighRank to write reviews and manage your startup's reputation." />
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[30rem] h-[18rem] rounded-full bg-emerald-500/10 blur-[110px]" />

      <FadeUp className="relative w-full max-w-md px-4 py-10">
        <div className="text-center mb-8">
          <Link to="/" data-testid="auth-logo" className="inline-flex mb-6" aria-label="HighRank.space home">
            <Logo />
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-slate-100">
            Back on the board
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Sign in to write reviews and manage your reputation.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-1 rounded-full border border-white/10 bg-[#0A0A09] p-1 mb-6">
          <button
            data-testid="auth-mode-login"
            className="rounded-full py-2 text-sm font-semibold transition-all duration-200 bg-emerald-500 text-black"
          >
            Sign in
          </button>
          <Link
            to="/add-business"
            data-testid="auth-mode-register"
            className="rounded-full py-2 text-sm font-semibold transition-all duration-200 text-slate-400 hover:text-slate-200 text-center"
          >
            Create account
          </Link>
        </div>

        <form onSubmit={submit} data-testid="auth-form" className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-7 space-y-4">
          <div>
            <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">Email</label>
            <input
              data-testid="auth-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              placeholder="you@company.com"
              className={inputCls}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300">Password</label>
              <Link
                to="/forgot-password"
                data-testid="auth-forgot-password-link"
                className="text-[11px] text-emerald-700 hover:text-emerald-600 transition-colors duration-200"
              >
                Forgot password?
              </Link>
            </div>
            <input
              data-testid="auth-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              placeholder="••••••••"
              className={inputCls}
            />
          </div>
          {error && (
            <p data-testid="auth-form-error" className="text-sm text-red-400">
              {error}
            </p>
          )}
          <label className="flex items-start gap-2.5 cursor-pointer group">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="auth-privacy-agree-checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-[#121210] text-emerald-500 accent-emerald-500 focus:ring-1 focus:ring-emerald-500/40 cursor-pointer"
            />
            <span className="text-[12px] leading-relaxed text-slate-400 group-hover:text-slate-300 transition-colors">
              I agree to the{" "}
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="auth-privacy-policy-link"
                className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
              >
                Privacy Policy
              </Link>
            </span>
          </label>
          <button
            type="submit"
            data-testid="auth-submit-button"
            disabled={submitting || !agreed}
            className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold rounded-full py-3 transition-all duration-200 hover:shadow-[0_0_25px_rgba(16,185,129,0.4)]"
          >
            {submitting ? "Hold on…" : "Sign in"}
          </button>
        </form>
      </FadeUp>
    </main>
  );
}
